import React, { useState } from 'react';

// ── All dropdown options ──────────────────────────────────────

const VEHICLE_CATEGORIES = [
  { id: 'passenger_car', name: 'Passenger Car' },
  { id: 'suv',           name: 'SUV' },
  { id: 'crossover',     name: 'Crossover' },
  { id: 'pickup',        name: 'Pickup' },
  { id: 'van',           name: 'Van' },
  { id: 'minivan',       name: 'Minivan / MPV' },
  { id: 'wagon',         name: 'Wagon' },
  { id: 'coupe',         name: 'Coupe' },
  { id: 'convertible',   name: 'Convertible' },
  { id: 'hatchback',     name: 'Hatchback' },
  { id: 'sports_car',    name: 'Sports Car' },
  { id: 'truck',         name: 'Truck' },
  { id: 'mini_truck',    name: 'Mini Truck / Kei Truck' },
  { id: 'dump_truck',    name: 'Dump Truck' },
  { id: 'crane_truck',   name: 'Crane Truck' },
  { id: 'mixer_truck',   name: 'Mixer Truck' },
  { id: 'tanker_truck',  name: 'Tanker Truck' },
  { id: 'tow_truck',     name: 'Tow Truck / Wrecker' },
  { id: 'fire_truck',    name: 'Fire Truck' },
  { id: 'garbage_truck', name: 'Garbage Truck' },
  { id: 'refrigerated_truck', name: 'Refrigerated Truck' },
  { id: 'flatbed_truck', name: 'Flatbed Truck' },
  { id: 'car_carrier',   name: 'Car Carrier' },
  { id: 'bus',           name: 'Bus' },
  { id: 'microbus',      name: 'Microbus' },
  { id: 'coach',         name: 'Coach / Tour Bus' },
  { id: 'school_bus',    name: 'School Bus' },
  { id: 'ambulance',     name: 'Ambulance' },
  { id: 'excavator',     name: 'Excavator' },
  { id: 'bulldozer',     name: 'Bulldozer' },
  { id: 'wheel_loader',  name: 'Wheel Loader' },
  { id: 'forklift',      name: 'Forklift' },
  { id: 'crane',         name: 'Crane (Mobile)' },
  { id: 'tractor',       name: 'Tractor' },
  { id: 'generator',     name: 'Generator' },
  { id: 'compressor',    name: 'Compressor' },
  { id: 'machinery',     name: 'Other Machinery' },
  { id: 'motorcycle',    name: 'Motorcycle' },
  { id: 'atv',           name: 'ATV / Side-by-Side' },
  { id: 'boat',          name: 'Boat / Marine' },
  { id: 'trailer',       name: 'Trailer' },
  { id: 'other',         name: 'Other' },
];

const TRUCK_TYPES = [
  'cargo','dump','crane','mixer','tanker','flatbed','wing_body','refrigerated',
  'tow','garbage','fire','vacuum','tipper','curtain_side','box','car_carrier',
];
const TONNAGES = ['1t','1.5t','2t','3t','3.5t','4t','6t','8t','10t','13t','15t','20t','25t'];
const CABIN_TYPES = ['single','double','crew','wide','narrow','high_roof'];
const DECK_TYPES = ['high_deck','low_deck','standard','flat','no_deck'];
const BED_OPTIONS = ['with_bed','without_bed','flatbed'];
const TRUCK_DRIVES = ['2WD','4WD','6x2','6x4','8x4'];
const GEAR_TYPES = ['short','long'];
const BRAKE_TYPES = ['oil','air','air_over_hydraulic'];
const AXLE_CONFIGS = ['single_rear','tandem','tridem','lift_axle'];
const CRANE_MFRS = ['Tadano','Unic','Maeda','Furukawa','other'];
const CRANE_POSITIONS = ['behind_cab','rear','mid'];
const WING_OPENINGS = ['left','right','both_sides'];

const BUS_TYPES = ['microbus','minibus','city_bus','coach','school_bus','shuttle','tour','commuter','double_decker'];
const SEAT_LAYOUTS = ['2x1','2x2','2x3','mixed'];
const SEAT_MATERIALS = ['fabric','leather','vinyl'];
const DOOR_CONFIGS = ['front_only','front_and_middle','front_and_rear','front_middle_rear'];
const SUSPENSIONS = ['leaf','air','mixed'];

const BODY_STYLES = ['sedan','suv','hatchback','wagon','coupe','convertible','pickup','minivan'];
const CAMERA_OPTIONS = ['none','rear','360'];

// ── Helpers ───────────────────────────────────────────────────

function Dropdown({ label, value, options, onChange }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <select className="form-select" value={value || ''} onChange={e => onChange(e.target.value)}>
        <option value="">Select</option>
        {options.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
      </select>
    </div>
  );
}

function NumberInput({ label, value, onChange, placeholder }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <input type="number" className="form-input" value={value || ''} placeholder={placeholder}
        onChange={e => onChange(e.target.value ? Number(e.target.value) : '')} />
    </div>
  );
}

function TextInput({ label, value, onChange, placeholder }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <input className="form-input" value={value || ''} placeholder={placeholder} onChange={e => onChange(e.target.value)} />
    </div>
  );
}

function Toggle({ label, value, onChange }) {
  return (
    <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)}
        style={{ width: 16, height: 16, accentColor: 'var(--blue)' }} />
      <label className="form-label" style={{ margin: 0 }}>{label}</label>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────

function VehicleExtraSpecs({ category, extraSpecs, onChange }) {
  const [expanded, setExpanded] = useState(true);

  const set = (key, val) => onChange({ ...extraSpecs, [key]: val });
  const v = (key) => extraSpecs?.[key] ?? '';

  const TRUCK_CATS = ['truck', 'mini_truck', 'dump_truck', 'crane_truck', 'mixer_truck', 'tanker_truck', 'tow_truck', 'fire_truck', 'garbage_truck', 'refrigerated_truck', 'flatbed_truck', 'car_carrier'];
  const BUS_CATS = ['bus', 'microbus', 'coach', 'school_bus'];
  const CAR_CATS = ['passenger_car', 'suv', 'crossover', 'pickup', 'van', 'minivan', 'wagon', 'coupe', 'convertible', 'hatchback', 'sports_car'];
  const isTruck = TRUCK_CATS.includes(category);
  const isBus = BUS_CATS.includes(category);
  const isCar = CAR_CATS.includes(category);

  if (!isTruck && !isBus && !isCar) return null;

  return (
    <div style={{ marginTop: 16, border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
      <button type="button" onClick={() => setExpanded(!expanded)} style={{
        width: '100%', padding: '12px 16px', background: 'var(--bg3)',
        border: 'none', color: 'var(--text)', fontSize: 13, fontWeight: 700,
        cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', fontFamily: 'inherit',
      }}>
        <span>Detailed Specs — {VEHICLE_CATEGORIES.find(c => c.id === category)?.name || category}</span>
        <span style={{ fontSize: 11, color: 'var(--text3)' }}>{expanded ? '▲ Collapse' : '▼ Expand'}</span>
      </button>

      {expanded && (
        <div style={{ padding: 16 }}>

          {/* ── Truck / Mini Truck ── */}
          {isTruck && (
            <>
              <div className="form-row">
                <Dropdown label="Truck Type" value={v('truck_type')} options={TRUCK_TYPES} onChange={val => set('truck_type', val)} />
                <Dropdown label="Tonnage" value={v('tonnage')} options={TONNAGES} onChange={val => set('tonnage', val)} />
                <Dropdown label="Cabin Type" value={v('cabin_type')} options={CABIN_TYPES} onChange={val => set('cabin_type', val)} />
              </div>
              <div className="form-row">
                <Dropdown label="Deck Type" value={v('deck_type')} options={DECK_TYPES} onChange={val => set('deck_type', val)} />
                <Dropdown label="Bed" value={v('bed')} options={BED_OPTIONS} onChange={val => set('bed', val)} />
                <Dropdown label="Drive System" value={v('drive_system')} options={TRUCK_DRIVES} onChange={val => set('drive_system', val)} />
              </div>
              <div className="form-row">
                <Dropdown label="Gear Type" value={v('gear_type')} options={GEAR_TYPES} onChange={val => set('gear_type', val)} />
                <Dropdown label="Brake Type" value={v('brake_type')} options={BRAKE_TYPES} onChange={val => set('brake_type', val)} />
                <Dropdown label="Axle Config" value={v('axle_config')} options={AXLE_CONFIGS} onChange={val => set('axle_config', val)} />
              </div>
              <div className="form-row">
                <NumberInput label="Tyre Bolts" value={v('tyre_bolts')} onChange={val => set('tyre_bolts', val)} placeholder="e.g. 6, 8, 10" />
                <NumberInput label="Body Length (m)" value={v('body_length_m')} onChange={val => set('body_length_m', val)} />
                <NumberInput label="Body Width (m)" value={v('body_width_m')} onChange={val => set('body_width_m', val)} />
              </div>
              <div className="form-row">
                <TextInput label="Tyre Size (Front)" value={v('tyre_size_front')} onChange={val => set('tyre_size_front', val)} placeholder="e.g. 205/85R16" />
                <TextInput label="Tyre Size (Rear)" value={v('tyre_size_rear')} onChange={val => set('tyre_size_rear', val)} placeholder="e.g. 205/85R16" />
              </div>
              <div className="form-row" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}>
                <Toggle label="Turbo" value={v('turbo')} onChange={val => set('turbo', val)} />
                <Toggle label="Intercooler" value={v('intercooler')} onChange={val => set('intercooler', val)} />
                <Toggle label="PTO" value={v('pto')} onChange={val => set('pto', val)} />
                <Toggle label="Exhaust Brake" value={v('exhaust_brake')} onChange={val => set('exhaust_brake', val)} />
                <Toggle label="Retarder" value={v('retarder')} onChange={val => set('retarder', val)} />
                <Toggle label="Spare Tyre" value={v('spare_tyre')} onChange={val => set('spare_tyre', val)} />
              </div>

              {/* Crane sub-fields */}
              {v('truck_type') === 'crane' && (
                <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 11, color: 'var(--blue)', fontWeight: 700, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Crane Details</div>
                  <div className="form-row">
                    <Dropdown label="Crane Manufacturer" value={v('crane_manufacturer')} options={CRANE_MFRS} onChange={val => set('crane_manufacturer', val)} />
                    <NumberInput label="Capacity (tons)" value={v('crane_capacity_tons')} onChange={val => set('crane_capacity_tons', val)} />
                    <NumberInput label="Boom Sections" value={v('boom_sections')} onChange={val => set('boom_sections', val)} />
                  </div>
                  <div className="form-row">
                    <NumberInput label="Boom Length (m)" value={v('boom_length_m')} onChange={val => set('boom_length_m', val)} />
                    <Dropdown label="Crane Position" value={v('crane_position')} options={CRANE_POSITIONS} onChange={val => set('crane_position', val)} />
                  </div>
                  <Toggle label="Radio Remote" value={v('radio_remote')} onChange={val => set('radio_remote', val)} />
                </div>
              )}

              {/* Refrigerated sub-fields */}
              {v('truck_type') === 'refrigerated' && (
                <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 11, color: 'var(--blue)', fontWeight: 700, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Refrigeration Details</div>
                  <div className="form-row">
                    <TextInput label="Reefer Unit" value={v('reefer_unit')} onChange={val => set('reefer_unit', val)} placeholder="e.g. Denso, Thermo King" />
                    <NumberInput label="Min Temp (°C)" value={v('reefer_temp_min')} onChange={val => set('reefer_temp_min', val)} />
                    <NumberInput label="Max Temp (°C)" value={v('reefer_temp_max')} onChange={val => set('reefer_temp_max', val)} />
                  </div>
                  <Toggle label="Standby Unit" value={v('standby_unit')} onChange={val => set('standby_unit', val)} />
                </div>
              )}

              {/* Wing body sub-fields */}
              {v('truck_type') === 'wing_body' && (
                <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 11, color: 'var(--blue)', fontWeight: 700, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Wing Body Details</div>
                  <div className="form-row">
                    <Dropdown label="Wing Opening" value={v('wing_opening')} options={WING_OPENINGS} onChange={val => set('wing_opening', val)} />
                    <NumberInput label="Gate Capacity (kg)" value={v('gate_capacity_kg')} onChange={val => set('gate_capacity_kg', val)} />
                  </div>
                  <Toggle label="Power Gate" value={v('power_gate')} onChange={val => set('power_gate', val)} />
                </div>
              )}
            </>
          )}

          {/* ── Bus ── */}
          {isBus && (
            <>
              <div className="form-row">
                <Dropdown label="Bus Type" value={v('bus_type')} options={BUS_TYPES} onChange={val => set('bus_type', val)} />
                <NumberInput label="Seating Capacity" value={v('seating_capacity')} onChange={val => set('seating_capacity', val)} />
                <NumberInput label="Standing Capacity" value={v('standing_capacity')} onChange={val => set('standing_capacity', val)} />
              </div>
              <div className="form-row">
                <Dropdown label="Seat Layout" value={v('seat_layout')} options={SEAT_LAYOUTS} onChange={val => set('seat_layout', val)} />
                <Dropdown label="Seat Material" value={v('seat_material')} options={SEAT_MATERIALS} onChange={val => set('seat_material', val)} />
                <Dropdown label="Door Config" value={v('door_config')} options={DOOR_CONFIGS} onChange={val => set('door_config', val)} />
              </div>
              <div className="form-row">
                <Dropdown label="Suspension" value={v('suspension')} options={SUSPENSIONS} onChange={val => set('suspension', val)} />
                <Dropdown label="Brake Type" value={v('brake_type')} options={BRAKE_TYPES} onChange={val => set('brake_type', val)} />
              </div>
              <div className="form-row" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}>
                <Toggle label="Reclining Seats" value={v('reclining_seats')} onChange={val => set('reclining_seats', val)} />
                <Toggle label="Automatic Doors" value={v('automatic_doors')} onChange={val => set('automatic_doors', val)} />
                <Toggle label="Air Conditioning" value={v('air_conditioning')} onChange={val => set('air_conditioning', val)} />
                <Toggle label="Luggage Compartment" value={v('luggage_compartment')} onChange={val => set('luggage_compartment', val)} />
                <Toggle label="Wheelchair Access" value={v('wheelchair_accessible')} onChange={val => set('wheelchair_accessible', val)} />
                <Toggle label="Toilet" value={v('toilet')} onChange={val => set('toilet', val)} />
              </div>
            </>
          )}

          {/* ── Passenger Car / SUV / Pickup / Van ── */}
          {isCar && (
            <>
              <div className="form-row">
                <Dropdown label="Camera" value={v('camera')} options={CAMERA_OPTIONS} onChange={val => set('camera', val)} />
              </div>
              <div className="form-row" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}>
                <Toggle label="Sunroof" value={v('sunroof')} onChange={val => set('sunroof', val)} />
                <Toggle label="Leather Seats" value={v('leather_seats')} onChange={val => set('leather_seats', val)} />
                <Toggle label="Navigation" value={v('navigation')} onChange={val => set('navigation', val)} />
                <Toggle label="Keyless Entry" value={v('keyless')} onChange={val => set('keyless', val)} />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Export for use in DetailModal display
export { VEHICLE_CATEGORIES };

export default VehicleExtraSpecs;
